/**
 * Tag input component for entering a list of string values.
 *
 * Provides a UI for adding/removing tags with pattern support hints.
 *
 * @module components/ui/TagInput
 */

import { useState, KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';

export interface TagInputProps {
  /** Current list of tags */
  value: string[];
  /** Called when the tag list changes */
  onChange: (next: string[]) => void;

  /** Optional label for the input */
  label?: string;
  /** Optional helper/description text */
  helperText?: string;

  /** Placeholder for the input */
  placeholder?: string;

  /** Disable editing */
  disabled?: boolean;

  /** Validation function - returns error message or null */
  validate?: (value: string) => string | null;
}

/**
 * A tag input component for entering a list of values (emails, usernames, etc.).
 * Supports adding via Enter key or Add button, and removing via X button.
 */
export function TagInput({
  value,
  onChange,
  label,
  helperText,
  placeholder = 'Enter a value',
  disabled = false,
  validate,
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;

    // Check for duplicates (case-insensitive)
    if (value.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
      setError('This value already exists');
      return;
    }

    // Run custom validation
    if (validate) {
      const validationError = validate(trimmed);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    onChange([...value, trimmed]);
    setDraft('');
    setError(null);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}

      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={handleAdd}
          disabled={disabled || !draft.trim()}
          aria-label="Add tag"
          className="shrink-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      {helperText && !error && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}

      {/* Tags display */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {value.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md"
            >
              <span className="max-w-[200px] truncate">{tag}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  aria-label={`Remove ${tag}`}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
